import type React from "react";

import { type BackgroundJob } from "../BackgroundJobsContext";

/**
 * Configuration for rendering a specific job type in the Jobs page.
 * Register custom renderers to control how your job type appears and
 * how its results are displayed.
 */
export interface JobTypeRenderer {
  /**
   * Human-readable label for this job type (e.g., "Entity Import", "Data Export")
   * Shown in the job type column of the Jobs page.
   */
  typeLabel: string;

  /**
   * Render custom details in the job row (chips, badges, progress, etc.)
   * This appears after the standard status/timestamp info.
   *
   * @example
   * renderRowDetails: (job) => (
   *   <>
   *     <Chip label={`${job.metadata?.fileSize} MB`} size="small" />
   *     <Chip label={job.metadata?.format} size="small" />
   *   </>
   * )
   */
  renderRowDetails?: (job: BackgroundJob) => React.ReactNode;

  /**
   * Component to render when "View Results" is clicked.
   * If not provided, falls back to URL navigation (if viewResultsUrl is set).
   *
   * @example
   * ResultsViewer: ({ jobId, onClose }) => (
   *   <MyCustomResultsDialog jobId={jobId} onClose={onClose} />
   * )
   */
  ResultsViewer?: React.ComponentType<{
    jobId: string;
    job: BackgroundJob;
    onClose: () => void;
  }>;
}

// Internal registry storage
const registry = new Map<string, JobTypeRenderer>();

/**
 * Register a renderer for a job type.
 * Call this at module load time (e.g., in your feature's index.ts).
 *
 * @example
 * // In src/features/exports/index.ts
 * import { registerJobTypeRenderer } from "src/contexts/backgroundJobs/jobTypeRegistry";
 * import ExportResultsViewer from "./ExportResultsViewer";
 *
 * registerJobTypeRenderer("DATA_EXPORT", {
 *   typeLabel: "Data Export",
 *   renderRowDetails: (job) => (
 *     <Chip label={job.metadata?.format} size="small" />
 *   ),
 *   ResultsViewer: ExportResultsViewer,
 * });
 */
export function registerJobTypeRenderer(
  jobType: string,
  renderer: JobTypeRenderer
): void {
  if (registry.has(jobType)) {
    console.warn(
      `[JobTypeRegistry] Overwriting existing renderer for job type: ${jobType}`
    );
  }
  registry.set(jobType, renderer);
}

/**
 * Get the registered renderer for a job type.
 * Returns undefined if no renderer is registered.
 */
export function getJobTypeRenderer(
  jobType: string
): JobTypeRenderer | undefined {
  return registry.get(jobType);
}

/**
 * Check if a job type has a registered renderer.
 */
export function hasJobTypeRenderer(jobType: string): boolean {
  return registry.has(jobType);
}

/**
 * Get all registered job types (for debugging/admin purposes).
 */
export function getRegisteredJobTypes(): string[] {
  return Array.from(registry.keys());
}
